export default function BenefitsSection() {
  return (
    <section id="benefits" className="py-16 md:py-24 bg-secondary-50">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block text-xs font-medium text-accent-600 bg-accent-100 px-3 py-1 rounded-full mb-4 tracking-wide">
              WHY CHOOSE VIGORBUY
            </span>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-4">
              Why Users Prefer Vigorbuy{" "}
              <span className="text-accent-600">Spreadsheet</span>
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
              See how Vigorbuy Spreadsheet compares to traditional searching
              methods and why thousands of shoppers make the switch.
            </p>
          </div>

          {/* Visual Feature Showcase */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-secondary-100 group cursor-pointer">
              <img
                src="https://readdy.ai/api/search-image?query=Beautifully%20organized%20product%20catalog%20grid%20layout%20on%20cream%20surface%20with%20soft%20natural%20lighting%2C%20systematic%20arrangement%20of%20fashion%20items%20in%20clean%20rows%2C%20sage%20green%20dividers%20between%20categories%2C%20modern%20minimalist%20organization%20aesthetic%2C%20Western%20contemporary%20editorial%20photography%2C%20calm%20structured%20composition%2C%20premium%20retail%20visual%20merchandising%2C%20orderly%20product%20display%20with%20artistic%20sensibility&width=800&height=600&seq=benefit-visual-org-2026z&orientation=landscape"
                alt="Organized category structure - Vigorbuy Spreadsheet"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white text-sm font-semibold">Organized Category Structure</p>
                <p className="text-white/70 text-xs">14 categories in one view</p>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-secondary-100 group cursor-pointer">
              <img
                src="https://readdy.ai/api/search-image?query=Side%20by%20side%20product%20comparison%20concept%20with%20two%20fashion%20items%20displayed%20on%20split%20cream%20background%2C%20soft%20natural%20lighting%20highlighting%20details%2C%20clean%20minimalist%20comparison%20layout%2C%20modern%20editorial%20comparison%20photography%2C%20sophisticated%20neutral%20tones%2C%20Western%20contemporary%20retail%20aesthetic%2C%20balanced%20symmetrical%20composition%2C%20premium%20catalog%20quality%2C%20sharp%20focus%20on%20product%20differences&width=800&height=600&seq=benefit-visual-compare-2026z&orientation=landscape"
                alt="Easy product comparison - Vigorbuy Spreadsheet"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white text-sm font-semibold">Effortless Comparison</p>
                <p className="text-white/70 text-xs">Side-by-side evaluation</p>
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-secondary-100 group cursor-pointer">
              <img
                src="https://readdy.ai/api/search-image?query=Fresh%20updated%20fashion%20product%20display%20on%20clean%20light%20surface%20with%20calendar%20and%20refresh%20concept%2C%20soft%20morning%20light%20suggesting%20newness%2C%20sage%20green%20accent%20elements%2C%20modern%20editorial%20update%20announcement%20photography%2C%20Western%20contemporary%20retail%20aesthetic%2C%20clean%20composition%20with%20fresh%20feel%2C%20premium%20catalog%20style%2C%20bright%20airy%20atmosphere%2C%20organized%20refresh%20concept%20visual&width=800&height=600&seq=benefit-visual-update-2026z&orientation=landscape"
                alt="Regularly updated resources - Vigorbuy Spreadsheet"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white text-sm font-semibold">Always Updated</p>
                <p className="text-white/70 text-xs">Weekly fresh content</p>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto rounded-2xl border border-background-200/80">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-background-200/80 bg-background-50">
                  <th className="px-6 py-4 text-sm font-semibold text-foreground-800">
                    Feature
                  </th>
                  <th className="px-6 py-4 text-sm font-semibold text-accent-700">
                    <div className="flex items-center gap-2">
                      <i className="ri-leaf-line"></i>
                      Vigorbuy Spreadsheet
                    </div>
                  </th>
                  <th className="px-6 py-4 text-sm font-semibold text-foreground-500">
                    Traditional Search
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/60">
                {[
                  {
                    feature: "Fast Navigation",
                    vigorbuy: true,
                    traditional: false,
                  },
                  {
                    feature: "Organized Data",
                    vigorbuy: true,
                    traditional: false,
                  },
                  {
                    feature: "Category Structure",
                    vigorbuy: true,
                    traditional: "Limited",
                  },
                  {
                    feature: "Easy Comparison",
                    vigorbuy: true,
                    traditional: "Difficult",
                  },
                  {
                    feature: "Updated Resources",
                    vigorbuy: true,
                    traditional: "Manual",
                  },
                  {
                    feature: "Beginner Friendly",
                    vigorbuy: true,
                    traditional: false,
                  },
                  {
                    feature: "All in One Place",
                    vigorbuy: true,
                    traditional: false,
                  },
                  {
                    feature: "Free Access",
                    vigorbuy: true,
                    traditional: "Varies",
                  },
                ].map((row, idx) => (
                  <tr
                    key={row.feature}
                    className={`${
                      idx % 2 === 0 ? "bg-background-50" : "bg-secondary-50/50"
                    } hover:bg-accent-50/30 transition-colors`}
                  >
                    <td className="px-6 py-3.5 text-sm font-medium text-foreground-700">
                      {row.feature}
                    </td>
                    <td className="px-6 py-3.5">
                      {row.vigorbuy === true ? (
                        <span className="inline-flex items-center gap-1.5 text-accent-600 text-sm font-medium">
                          <i className="ri-checkbox-circle-fill text-base"></i>
                          Yes
                        </span>
                      ) : (
                        <span className="text-sm text-foreground-700">
                          {row.vigorbuy}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-3.5">
                      {row.traditional === false ? (
                        <span className="inline-flex items-center gap-1.5 text-foreground-400 text-sm">
                          <i className="ri-close-circle-line text-base"></i>
                          No
                        </span>
                      ) : (
                        <span className="text-sm text-foreground-500">
                          {row.traditional}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Key Features Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
            {[
              { icon: "ri-folder-open-line", label: "Category Organization" },
              { icon: "ri-navigation-line", label: "Easy Navigation" },
              { icon: "ri-scales-3-line", label: "Quick Comparison" },
              { icon: "ri-user-heart-line", label: "Beginner Friendly" },
              { icon: "ri-loop-left-line", label: "Updated Resources" },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-3 px-4 py-3.5 bg-background-50 rounded-xl border border-background-200/60"
              >
                <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-accent-600 text-sm`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-700">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}